import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Sparkles, Zap, CheckCircle } from 'lucide-react';
import './ATSLoadingPage.css';

const ATSLoadingPage = ({ theme }) => {
  const steps = [
    { icon: FileText, label: 'Scanning Resume', delay: 0 },
    { icon: Sparkles, label: 'Extracting Skills', delay: 1.2 },
    { icon: Zap, label: 'Analyzing Match', delay: 2.4 },
    { icon: CheckCircle, label: 'Generating Report', delay: 3.6 }
  ];

  return (
    <div className={`ats-loading-page ${theme}`}>
      <div className="loading-container">
        {/* Animated Scanner */}
        <motion.div 
          className="scanner-wrapper"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="scanner-document"
            animate={{ 
              y: [0, -8, 0],
              rotateY: [0, 3, 0, -3, 0]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <FileText size={60} color="#667eea" />
            
            {/* Scanning Line */}
            <motion.div
              className="scan-line"
              animate={{ y: [-30, 30] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "linear"
              }}
            />
          </motion.div>

          {/* Particles */}
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="particle"
              style={{
                left: `${50 + Math.cos(i * 45 * Math.PI / 180) * 80}px`,
                top: `${50 + Math.sin(i * 45 * Math.PI / 180) * 80}px`,
              }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.2,
              }}
            />
          ))}
        </motion.div>

        {/* Main Title */}
        <motion.h1 
          className="loading-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          Analyzing Your Resume
        </motion.h1>

        <motion.p 
          className="loading-subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Our AI is processing your resume
        </motion.p>

        {/* Progress Steps */}
        <div className="progress-steps">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="progress-step"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 + index * 0.15 }}
            >
              <motion.div 
                className="step-icon"
                animate={{ 
                  scale: [1, 1.1, 1],
                }}
                transition={{ 
                  duration: 1.5,
                  repeat: Infinity,
                  delay: step.delay
                }}
              >
                <step.icon size={20} />
              </motion.div>
              <span className="step-label">{step.label}</span>
              
              {/* Animated Checkmark */}
              <motion.div
                className="step-check"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  delay: 1.5 + step.delay, // ← CHANGED: slower checkmarks
                  duration: 0.3
                }}
              >
                ✓
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="progress-bar-container">
          <motion.div 
            className="progress-bar"
            initial={{ width: '0%' }}
            animate={{ width: '99%' }}
            transition={{ 
              duration: 9, // ← CHANGED: 5 seconds
              ease: "easeInOut"
            }}
          />
        </div>

        {/* Percentage Counter */}
        <motion.div 
          className="progress-percentage"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Counter from={0} to={96} duration={9000} />%
          {/* ↑ CHANGED: 5000ms = 5 seconds */}
        </motion.div>

        {/* Fun Facts */}
        <motion.div 
          className="loading-tips"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <Sparkles size={14} />
          <span>75% of resumes are rejected by ATS systems</span>
        </motion.div>
      </div>
    </div>
  );
};

// Counter Component
const Counter = ({ from, to, duration }) => {
  const [count, setCount] = React.useState(from);

  React.useEffect(() => {
    let start = from;
    const end = to;
    const increment = (end - start) / (duration / 50);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 50);

    return () => clearInterval(timer);
  }, [from, to, duration]);

  return <>{count}</>;
};

export default ATSLoadingPage;
